// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Bills.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Gestão de Pagamento de Faturas/Serviços
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Receipt,
  TrendingUp,
  Bolt,
  Water,
  Wifi,
  Tv,
  Refresh,
  FileDownload,
  Visibility,
  Edit,
  Add,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService, { BillPayment, ServiceProvider, BillPaymentFilters } from '../../services/banking.service';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  electricity: <Bolt />,
  water: <Water />,
  telecom: <Wifi />,
  internet: <Wifi />,
  tv: <Tv />,
};

const STATUS_MAP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  pending: { label: 'Pendente', color: 'warning' },
  processing: { label: 'Processando', color: 'info' },
  completed: { label: 'Concluído', color: 'success' },
  failed: { label: 'Falhou', color: 'error' },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const BankingBills: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);

  // Payments state
  const [payments, setPayments] = useState<BillPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Providers state
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [providerForm, setProviderForm] = useState({
    name: '',
    category: '',
    referenceFormat: '',
    referenceLength: 10,
    fee: 0,
    feeType: 'fixed' as 'fixed' | 'percentage',
    active: true,
  });

  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    totalAmount: 0,
    totalFees: 0,
  });

  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const filters: BillPaymentFilters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(categoryFilter !== 'all' && { category: categoryFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      };

      const response = await bankingService.getBillPayments(filters);
      setPayments(response.data);
      setTotalCount(response.pagination?.total || 0);

      if (response.summary) {
        setStats({
          total: response.summary.totalPayments || 0,
          totalAmount: response.summary.totalAmount || 0,
          totalFees: response.summary.totalFees || 0,
        });
      }
    } catch (err: any) {
      setError(err.message || t('banking.bills.errorLoadingPayments'));
    } finally {
      setLoadingPayments(false);
    }
  }, [page, rowsPerPage, searchQuery, categoryFilter, statusFilter]);

  const fetchProviders = useCallback(async () => {
    setLoadingProviders(true);
    try {
      const data = await bankingService.getProviders();
      setProviders(data);
    } catch (err: any) {
      setError(err.message || t('banking.bills.errorLoadingProviders'));
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchPayments();
    } else {
      fetchProviders();
    }
  }, [tabValue, fetchPayments, fetchProviders]);

  const handleSaveProvider = async () => {
    try {
      if (editingProvider) {
        await bankingService.updateProvider(editingProvider.id, providerForm);
      } else {
        await bankingService.createProvider(providerForm);
      }
      setProviderDialogOpen(false);
      fetchProviders();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleProvider = async (id: number) => {
    try {
      await bankingService.toggleProviderStatus(id);
      fetchProviders();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const paymentColumns: Column<BillPayment>[] = [
    { id: 'id', label: 'ID', format: (_, row) => row.id.slice(0, 8) + '...' },
    {
      id: 'provider',
      label: 'Provedor',
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {CATEGORY_ICONS[row.providerCategory] || <Receipt />}
          <Typography variant="body2">{row.providerName}</Typography>
        </Box>
      ),
    },
    { id: 'customerName', label: 'Cliente' },
    { id: 'reference', label: 'Referência', format: (_, row) => <code>{row.reference}</code> },
    { id: 'amount', label: 'Valor', align: 'right', format: (_, row) => <CurrencyCell value={row.amount} /> },
    {
      id: 'status',
      label: 'Estado',
      format: (_, row) => {
        const status = STATUS_MAP[row.status];
        return <Chip label={status?.label || row.status} size="small" color={status?.color || 'default'} />;
      },
    },
    { id: 'createdAt', label: 'Data', format: (_, row) => <DateCell value={row.createdAt} /> },
  ];

  const providerColumns: Column<ServiceProvider>[] = [
    { id: 'id', label: 'ID' },
    { id: 'name', label: 'Nome' },
    {
      id: 'category',
      label: 'Categoria',
      format: (_, row) => (
        <Chip
          size="small"
          icon={CATEGORY_ICONS[row.category] as any}
          label={row.category}
        />
      ),
    },
    { id: 'referenceFormat', label: 'Formato Ref.' },
    {
      id: 'fee',
      label: 'Taxa',
      format: (_, row) => row.feeType === 'fixed' ? formatCurrency(row.fee) : `${row.fee}%`,
    },
    {
      id: 'active',
      label: 'Ativo',
      format: (_, row) => (
        <Switch
          checked={row.active}
          onChange={() => handleToggleProvider(row.id)}
          size="small"
        />
      ),
    },
    {
      id: 'stats',
      label: 'Transações',
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.totalTransactions}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCurrency(row.totalAmount)}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'actions',
      label: 'Ações',
      format: (_, row) => (
        <IconButton
          size="small"
          onClick={() => {
            setEditingProvider(row);
            setProviderForm({
              name: row.name,
              category: row.category,
              referenceFormat: row.referenceFormat,
              referenceLength: row.referenceLength,
              fee: row.fee,
              feeType: row.feeType,
              active: row.active,
            });
            setProviderDialogOpen(true);
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          {t('banking.bills.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {tabValue === 1 && (
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => {
                setEditingProvider(null);
                setProviderForm({
                  name: '',
                  category: '',
                  referenceFormat: '',
                  referenceLength: 10,
                  fee: 0,
                  feeType: 'fixed',
                  active: true,
                });
                setProviderDialogOpen(true);
              }}
            >
              {t('banking.bills.newProvider')}
            </Button>
          )}
          <Button startIcon={<Refresh />} variant="outlined" onClick={tabValue === 0 ? fetchPayments : fetchProviders}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">{t('banking.bills.totalPayments')}</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">{t('banking.bills.totalValueLabel')}</Typography>
              <Typography variant="h4" fontWeight="bold">{formatCurrency(stats.totalAmount)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">{t('banking.bills.feesCharged')}</Typography>
              <Typography variant="h4" fontWeight="bold">{formatCurrency(stats.totalFees)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('banking.bills.paymentsTab')} />
          <Tab label={t('banking.bills.providersTab')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pesquisar"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Categoria"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="electricity">Eletricidade</MenuItem>
                  <MenuItem value="water">Água</MenuItem>
                  <MenuItem value="telecom">Telecomunicações</MenuItem>
                  <MenuItem value="internet">Internet</MenuItem>
                  <MenuItem value="tv">TV</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="completed">Concluído</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="failed">Falhou</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {loadingPayments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              columns={paymentColumns}
              data={payments}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loadingProviders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              columns={providerColumns}
              data={providers}
              page={0}
              rowsPerPage={100}
              totalCount={providers.length}
              onPageChange={() => {}}
              onRowsPerPageChange={() => {}}
            />
          )}
        </TabPanel>
      </Card>

      {/* Provider Dialog */}
      <Dialog open={providerDialogOpen} onClose={() => setProviderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProvider ? t('banking.bills.editProvider') : t('banking.bills.newProvider')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={providerForm.name}
                onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Categoria"
                value={providerForm.category}
                onChange={(e) => setProviderForm({ ...providerForm, category: e.target.value })}
              >
                <MenuItem value="electricity">Eletricidade</MenuItem>
                <MenuItem value="water">Água</MenuItem>
                <MenuItem value="telecom">Telecomunicações</MenuItem>
                <MenuItem value="internet">Internet</MenuItem>
                <MenuItem value="tv">TV</MenuItem>
                <MenuItem value="insurance">Seguros</MenuItem>
                <MenuItem value="government">Governo</MenuItem>
                <MenuItem value="other">Outros</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Formato da Referência"
                placeholder="Ex: NNNNNNNNNN"
                value={providerForm.referenceFormat}
                onChange={(e) => setProviderForm({ ...providerForm, referenceFormat: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Tamanho da Referência"
                value={providerForm.referenceLength}
                onChange={(e) => setProviderForm({ ...providerForm, referenceLength: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Taxa"
                value={providerForm.fee}
                onChange={(e) => setProviderForm({ ...providerForm, fee: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo de Taxa"
                value={providerForm.feeType}
                onChange={(e) => setProviderForm({ ...providerForm, feeType: e.target.value as 'fixed' | 'percentage' })}
              >
                <MenuItem value="fixed">Fixa (CVE)</MenuItem>
                <MenuItem value="percentage">Percentual (%)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={providerForm.active}
                    onChange={(e) => setProviderForm({ ...providerForm, active: e.target.checked })}
                  />
                }
                label="Ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProviderDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveProvider}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingBills;
